import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { useGuardedContext } from '@/hooks/useGuardedContext';
import { zIndexes } from '@/styles/zIndexes';

import { useNavigateToSlide } from '../ClientPresentationDesigner.hooks';

type DirtyGetter = () => boolean;
type DiscardHandler = () => void;

interface DirtyRegistration {
  isDirty: DirtyGetter;
  onDiscard?: DiscardHandler;
}

interface SlideNavGuardContextShape {
  /**
   * Register a function that reports whether an owning component has
   * unsaved changes, and an optional handler invoked when the user
   * chooses "Discard changes" in the confirmation modal (e.g., to
   * close a drawer whose form state has now become stale). Returns
   * an unregister function.
   */
  registerDirtyGetter: (registration: DirtyRegistration) => () => void;
  /**
   * Navigate to a slide, prompting with a confirmation modal first
   * if any registered getter reports unsaved changes.
   */
  guardedNavigateToSlide: (slideId: string) => void;
  /**
   * Run an action that would leave the current drawer (e.g., opening
   * another modal). Prompts first if any registered getter reports
   * unsaved changes; otherwise runs immediately.
   */
  confirmBeforeAction: (action: () => void) => void;
}

// Default is `null` so `useGuardedContext` actually throws when a consumer
// is rendered outside `SlideNavGuardProvider` instead of silently returning
// a stub where callbacks could be no-ops. Hooks that are intentionally
// safe outside the provider use raw `useContext` and handle `null` directly.
const SlideNavGuardContext = createContext<SlideNavGuardContextShape | null>(
  null
);

export function useSlideNavGuard() {
  return useGuardedContext(SlideNavGuardContext, 'SlideNavGuardContext');
}

/**
 * Subscribe an `isDirty` signal to the slide-nav guard for the lifetime
 * of the calling component. When `isDirty` is true, guarded navigation
 * and guarded actions will surface the unsaved-changes confirmation
 * modal before proceeding. An optional `onDiscard` handler is invoked
 * if the user chooses "Discard changes" — use it to reset the owning
 * drawer so it doesn't linger with a stale dirty form.
 *
 * Safe to call outside the provider (e.g., when a shared drawer is used
 * both inside and outside the presentation designer) — becomes a no-op.
 */
export function useRegisterSlideNavDirtyGetter(
  isDirty: boolean,
  onDiscard?: DiscardHandler
) {
  const ctx = useContext(SlideNavGuardContext);
  // Refs keep the registration identity stable across renders while
  // still reporting the latest values.
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const onDiscardRef = useRef(onDiscard);
  onDiscardRef.current = onDiscard;

  useEffect(() => {
    if (!ctx) return;
    return ctx.registerDirtyGetter({
      isDirty: () => isDirtyRef.current,
      onDiscard: () => onDiscardRef.current?.(),
    });
  }, [ctx]);
}

export function SlideNavGuardProvider({ children }: PropsWithChildren) {
  const { navigateToSlide } = useNavigateToSlide();
  const registrationsRef = useRef<Set<DirtyRegistration>>(new Set());
  const pendingActionRef = useRef<(() => void) | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const registerDirtyGetter = useCallback((registration: DirtyRegistration) => {
    registrationsRef.current.add(registration);
    return () => {
      registrationsRef.current.delete(registration);
    };
  }, []);

  const hasUnsavedChanges = useCallback(
    () => Array.from(registrationsRef.current).some((r) => r.isDirty()),
    []
  );

  const confirmBeforeAction = useCallback(
    (action: () => void) => {
      if (hasUnsavedChanges()) {
        pendingActionRef.current = action;
        setShowConfirmation(true);
      } else {
        action();
      }
    },
    [hasUnsavedChanges]
  );

  const guardedNavigateToSlide = useCallback(
    (slideId: string) => {
      confirmBeforeAction(() => navigateToSlide(slideId));
    },
    [confirmBeforeAction, navigateToSlide]
  );

  const handleConfirmationClose = useCallback(
    (reason: 'confirm' | 'cancel') => {
      setShowConfirmation(false);
      if (reason === 'cancel') {
        // "Discard changes" — let each dirty registrant clean up
        // (e.g., close its drawer so a stale form doesn't linger),
        // then run the stashed action.
        registrationsRef.current.forEach((r) => {
          if (r.isDirty()) r.onDiscard?.();
        });
        const pending = pendingActionRef.current;
        pendingActionRef.current = null;
        pending?.();
      } else {
        // "Stay" — drop the pending action.
        pendingActionRef.current = null;
      }
    },
    []
  );

  const value = useMemo<SlideNavGuardContextShape>(
    () => ({
      registerDirtyGetter,
      guardedNavigateToSlide,
      confirmBeforeAction,
    }),
    [registerDirtyGetter, guardedNavigateToSlide, confirmBeforeAction]
  );

  return (
    <SlideNavGuardContext.Provider value={value}>
      {children}
      {showConfirmation && (
        <ConfirmationModal
          isOpen
          heading="Unsaved changes"
          cancelText="Discard changes"
          confirmText="Stay"
          cancelButtonProps={{ variant: 'destructive-primary' }}
          confirmButtonProps={{ variant: 'secondary' }}
          closeOnOutsideClick={false}
          onClose={handleConfirmationClose}
          sx={{ zIndex: zIndexes.POPOVER + 100 }}
        >
          You have unsaved changes. Are you sure you want to continue? Your
          changes will not be saved.
        </ConfirmationModal>
      )}
    </SlideNavGuardContext.Provider>
  );
}
